import { Component, Input, OnChanges, OnDestroy, OnInit, SimpleChanges, ChangeDetectorRef } from '@angular/core';
import { getAssistantPromptByTag } from "../../utils/assistant-prompt.util";
import { NgClass, NgForOf, NgIf, NgSwitch, NgSwitchCase, NgSwitchDefault, NgTemplateOutlet } from "@angular/common";
import { PulseLoadingComponent } from "../../pages/report/pulse-loading/pulse-loading.component";
import { PopupService } from "../../services/popup.service";
import { safeParseJSON } from "../../utils/safe-parse-json.util";
import { PromptService } from "../../services/prompt.service";
import { SharedService } from "../../services/shared.service";
import { MatTooltip } from "@angular/material/tooltip";
import { AuthService } from "../../services/auth.service";
import { Subscription } from 'rxjs';

interface RoleResponse {
  job_level: string;
  identified_interests: string[];
}

interface NewsItem {
  date: string;
  title: string;
  short_description: string;
  long_description: string;
  url?: string;
}

// Add new interface for insight items
interface InsightItem {
  title: string;
  description: string;
}

// Add interface for the new insight response format
interface InsightResponse {
  short: string;
  long?: string;
  insights?: InsightItem[];
}

@Component({
  selector: 'app-assistant-prompt',
  standalone: true,
  imports: [
    NgIf,
    PulseLoadingComponent,
    NgClass,
    NgSwitch,
    NgSwitchDefault,
    NgSwitchCase,
    NgForOf,
    MatTooltip,
    NgTemplateOutlet,
  ],
  templateUrl: './assistant-prompt.component.html',
  styleUrl: './assistant-prompt.component.scss',
  styles: [`
    .animated-random-gradient-text {
      background: linear-gradient(90deg,
        var(--gradient-start),
        var(--gradient-mid),
        var(--gradient-end)
      );
      background-size: 400% auto;
      color: transparent;
      -webkit-background-clip: text;
      background-clip: text;
      animation: randomGradientShift 5s ease infinite;
    }
    .animated-dots::after {
      content: '...';
      display: inline-block;
      animation: loadingDots 1.5s infinite linear;
      overflow: hidden;
      color: transparent;
      vertical-align: bottom;
    }
    @keyframes randomGradientShift {
      0% { background-position: 0% 50%; }
      50% { background-position: 100% 50%; }
      100% { background-position: 0% 50%; }
    }
    @keyframes loadingDots {
      0%, 20% { content: '.'; }
      33%, 53% { content: '..'; }
      66%, 86% { content: '...'; }
    }
  `]
})
export class AssistantPromptComponent implements OnInit, OnChanges, OnDestroy {

  @Input() company: any; // The company object
  @Input() tag: string; // The assistant prompt tag to fetch
  @Input() title: string = null; // The assistant prompt title
  @Input() class: string; // Additional class for styling
  @Input() type: string | null = null; // Format: 'insights', 'html', 'list', etc.
  @Input() showButton: boolean = false; // Whether to show the button
  @Input() buttonType: number = 1; // Button type: 1 or 2
  @Input() options?: string[] = [];
  @Input() reportId: number; // Report ID for filtering WebSocket events

  @Input() size: string = 'size-2';
  @Input() color: string = 'blue-sky';

  promptContent: string | null = null;
  shortContent: string | null = null;
  longContent: string | null = null;

  // Track timestamp of last notification to prevent ngOnChanges from resetting after notification
  private lastNotificationTime = 0;
  htmlContent: string | null = null;
  listContent: string[] = [];
  yearsContent: string | null = null;
  specialContent: any = null;
  newsContent: NewsItem[] = [];
  companyDataContent: { short: string; long: string } | null = null;

  // Add new property for insights array
  insightsArray: InsightItem[] = [];

  roleSelections: boolean[] = [];
  jobLevel: string = '';
  identifiedInterests: string[] = [];

  user: any;

  private promptSubscription: Subscription | null = null;

  constructor(
    private popupService: PopupService,
    private promptService: PromptService,
    private sharedService: SharedService,
    private authService: AuthService,
    private cdr: ChangeDetectorRef
  ) {
    this.authService.authUser().subscribe(user => {
      this.user = user;
    });
  }

  ngOnInit(): void {
    this.setRandomGradient();

    // Process initial data
    this.initializePromptData();

    // Subscribe to real-time updates with report ID filtering
    this.promptSubscription = this.promptService.promptUpdate$.subscribe(({ tag, content, reportId }) => {
      // Only process events that match BOTH this tag AND this reportId.
      // Coerce to numbers: this.reportId is a parsed number but the event's
      // reportId / the bound report.id can be a string, and a strict ===
      // mismatch would silently drop every live insight update after a switch.
      if (tag === this.tag && Number(reportId) === Number(this.reportId)) {
        // Mark that we just received a notification (to prevent ngOnChanges from firing)
        this.lastNotificationTime = Date.now();

        // Reset state and process new data
        this.resetComponentState();
        this.processPromptData(content);
        // Manually trigger change detection (wrapped in try-catch for safety)
        try {
          this.cdr.detectChanges();
        } catch (error) {
          console.warn(`⚠️ [AssistantPrompt] Change detection failed`, { tag, error: error.message });
        }
      }
    });
  }

  ngOnChanges(changes: SimpleChanges): void {
    if (changes['company']) {
      // Skip first change as it's handled by ngOnInit
      if (changes['company'].firstChange) {
        return;
      }

      const previousCompany = changes['company'].previousValue;
      const currentCompany = changes['company'].currentValue;

      // The active company itself changed (company switch). Always clear the card
      // and re-read for the new company, BYPASSING the notification guard below.
      // Otherwise a recent same-tag notification (lastNotificationTime) can
      // suppress this reset and the card keeps showing the PREVIOUS company's
      // content during the window before the new company's data arrives.
      if (previousCompany?.id !== currentCompany?.id) {
        this.resetComponentState();
        this.initializePromptData();
        try {
          this.cdr.detectChanges();
        } catch (error) {
          console.warn(`⚠️ [AssistantPrompt] Change detection failed`, { tag: this.tag, error: error.message });
        }
        return;
      }

      // Same company: skip if we just received a notification in the last 200ms.
      // This prevents ngOnChanges from resetting state after a notification update.
      const timeSinceNotification = Date.now() - this.lastNotificationTime;
      if (timeSinceNotification < 200) {
        return;
      }

      // Check if the actual data has changed by comparing the insight data
      const previousData = this.getPromptDataFromCompany(previousCompany);
      const currentData = this.getPromptDataFromCompany(currentCompany);

      // If data has changed, reset and reinitialize
      const previousDataStr = JSON.stringify(previousData);
      const currentDataStr = JSON.stringify(currentData);

      if (previousDataStr !== currentDataStr) {
        this.resetComponentState();
        this.initializePromptData();
        // Manually trigger change detection (wrapped in try-catch for safety)
        try {
          this.cdr.detectChanges();
        } catch (error) {
          console.warn(`⚠️ [AssistantPrompt] Change detection failed`, { tag: this.tag, error: error.message });
        }
      }
    }
  }

  /**
   * Helper method to extract prompt data from company object
   */
  private getPromptDataFromCompany(company: any): any {
    if (!company) return null;
    return getAssistantPromptByTag(company, this.tag);
  }

  ngOnDestroy(): void {
    // Unsubscribe to prevent memory leaks
    if (this.promptSubscription) {
      this.promptSubscription.unsubscribe();
    }
  }

  setRandomGradient() {
    const gradients = [
      { start: '#8ac9e8', mid: '#5cb4dd', end: '#1c9ed1' },
    ];

    const randomGradient = gradients[Math.floor(Math.random() * gradients.length)];

    document.documentElement.style.setProperty('--gradient-start', randomGradient.start);
    document.documentElement.style.setProperty('--gradient-mid', randomGradient.mid);
    document.documentElement.style.setProperty('--gradient-end', randomGradient.end);
  }

  /**
   * Resets all component fields to their initial state.
   */
  private resetComponentState(): void {
    this.promptContent = null;
    this.shortContent = null;
    this.longContent = null;
    this.htmlContent = null;
    this.listContent = [];
    this.yearsContent = null;
    this.specialContent = null;
    this.insightsArray = []; // Reset insights array
    // Cleared too so the company-news / company-data cards return to their
    // loading state on a company switch (their templates gate on these fields).
    this.newsContent = [];
    this.companyDataContent = null;

    this.jobLevel = '';
    this.roleSelections = [];
    this.identifiedInterests = [];
  }

  /**
   * Initializes the component by fetching and processing the initial prompt data.
   */
  private initializePromptData(): void {
    // console.log(`[initializePromptData] Initializing for tag: ${this.tag}, type: ${this.type}`);

    const promptData = getAssistantPromptByTag(this.company, this.tag);
    // console.log(`[initializePromptData] Raw data for tag ${this.tag}:`, promptData);

    // Check if promptData is null (prompt not found)
    if (promptData === null) {
      if (this.type === 'insights' || this.type === 'basic_words') {
        this.shortContent = null;
        this.longContent = null;
        this.insightsArray = [];
      } else if (this.type === 'special-box') {
        this.htmlContent = null;
        this.specialContent = null;
      } else if (this.type === 'list' || this.type === 'pills') {
        this.listContent = [];
      } else if (this.type === 'years') {
        this.yearsContent = null;
      } else {
        this.promptContent = null;
      }
      return;
    }

    // Process the prompt data
    this.processPromptData(promptData);
  }

  /**
   * Processes the prompt data based on the `type`.
   *
   * @param promptData - The raw prompt data to process.
   */
  private processPromptData(promptData: any): void {
    /*
    console.log(`[processPromptData] Processing ${this.type} data type:`, typeof promptData);
    if (typeof promptData === 'string') {
      console.log(`[processPromptData] Data sample:`, promptData.slice(0, 100) + '...');
    } else if (typeof promptData === 'object') {
      console.log(`[processPromptData] Data keys:`, Object.keys(promptData));
    }
    */

    // Exit early if data is null
    if (promptData === null) {
      this.resetComponentState();
      return;
    }

    // PRE-PROCESS: Try to parse JSON strings for any type before passing to handlers
    if (typeof promptData === 'string' && promptData.trim().startsWith('{')) {
      try {
        const parsed = JSON.parse(promptData);
        if (typeof parsed === 'object' && parsed !== null) {
          // console.log(`[processPromptData] Successfully parsed JSON string`);
          promptData = parsed;
        }
      } catch (e) {
        console.error(`[processPromptData] Error parsing JSON:`, e);
        // Continue with the original string if parsing fails
      }
    }

    // Define handlers for each type
    const handlers: { [key: string]: (data: any) => void } = {
      insights: this.handleInsights.bind(this),
      'basic_words': this.handleInsights.bind(this),
      html: (data: any) => {
        this.htmlContent = typeof data === 'string' ? data : JSON.stringify(data);
      },
      list: (data: any) => {
        this.listContent = this.getFormattedList(data, 'interests');
      },
      pills: (data: any) => this.handlePills(data, 'interests'),
      years: (data: any) => {
        this.yearsContent = this.getFormattedYears(data);
      },
      'personal-background': this.handlePersonalBackground.bind(this),
      'special-box': this.handleSpecialBox.bind(this),
      'email-sequence': this.handleEmailSequence.bind(this),
      'exploratory-email': this.handleExploratoryEmail.bind(this),
      role: this.handleRole.bind(this),
      'role-2': this.handleRole.bind(this),
      'company-news': this.handleCompanyNews.bind(this),
      'company-data': this.handleCompanyData.bind(this),
    };

    // Execute the appropriate handler based on type
    if (this.type && handlers[this.type]) {
      // console.log(`[processPromptData] Using handler for type: ${this.type}`);
      handlers[this.type](promptData);
    } else {
      // console.log(`[processPromptData] No handler found for type: ${this.type}, using default`);

      // Default handling for unsupported or missing types
      this.promptContent = typeof promptData === 'string' ? promptData : JSON.stringify(promptData);
    }
  }

  /**
   * Handles insights format by properly extracting short and long content.
   * Updated to handle the new insights array format and basic_words format.
   */
  private handleInsights(data: any): void {
    // console.log('[handleInsights] Input type:', typeof data);
    // console.log('[handleInsights] Raw data:', data);

    // Reset content
    this.shortContent = null;
    this.longContent = null;
    this.insightsArray = [];

    // If data is null, return early
    if (data === null) {
      return;
    }

    try {
      // Case 1: If data is already an object
      if (typeof data === 'object' && data !== null) {
        // Check for nested response format (common for basic_words and other insights)
        if ('response' in data && typeof data.response === 'object' && data.response !== null) {
          const responseData = data.response;
          // Check for insights array in response
          if ('insights' in responseData && Array.isArray(responseData.insights)) {
            this.shortContent = responseData.short || 'View insights';
            this.insightsArray = responseData.insights;
            if ('long' in responseData) {
              this.longContent = responseData.long;
            }
            // console.log('[handleInsights] Found insights array in response with', this.insightsArray.length, 'items');
            return;
          }
          // Check for short/long in response
          if ('short' in responseData) {
            this.shortContent = responseData.short;
            this.longContent = responseData.long || responseData.short;
            // console.log('[handleInsights] Found short/long in response:', { short: this.shortContent?.substring(0, 50), long: this.longContent?.substring(0, 50) });
            return;
          }
        }

        // Check for insights array format at root level
        if ('insights' in data && Array.isArray(data.insights)) {
          this.shortContent = data.short || 'View insights';
          this.insightsArray = data.insights;
          // If there's also a long property, store it
          if ('long' in data) {
            this.longContent = data.long;
          }
          // console.log('[handleInsights] Found insights array with', this.insightsArray.length, 'items');
          return;
        }

        // Original format: short/long at root level
        if ('short' in data) {
          this.shortContent = data.short;
          this.longContent = data.long || data.short;
          // console.log('[handleInsights] Found short/long format at root:', { short: this.shortContent?.substring(0, 50), long: this.longContent?.substring(0, 50) });
          return;
        }
      }

      // Case 2: If data looks like JSON string (most common case for basic_words)
      if (typeof data === 'string') {
        // First, try to parse as JSON if it looks like JSON
        if (data.trim().startsWith('{')) {
          try {
            const parsed = JSON.parse(data);
            // console.log('[handleInsights] Successfully parsed JSON string:', parsed);

            if (typeof parsed === 'object' && parsed !== null) {
              // Check for insights array in parsed data
              if ('insights' in parsed && Array.isArray(parsed.insights)) {
                this.shortContent = parsed.short || 'View insights';
                this.insightsArray = parsed.insights;
                if ('long' in parsed) {
                  this.longContent = parsed.long;
                }
                // console.log('[handleInsights] Found insights array in parsed JSON');
                return;
              }

              // Original format in parsed data (basic_words format)
              if ('short' in parsed) {
                this.shortContent = parsed.short;
                this.longContent = parsed.long || parsed.short;
                // console.log('[handleInsights] Found basic_words format in parsed JSON:', { short: this.shortContent?.substring(0, 50), long: this.longContent?.substring(0, 50) });
                return;
              }
            }
          } catch (e) {
            console.error('[handleInsights] Failed to parse JSON:', e);

            // AGGRESSIVE FIX: Try to manually extract the content using regex
            const shortMatch = data.match(/"short"\s*:\s*"([^"]*)"/);
            const longMatch = data.match(/"long"\s*:\s*"([^"]*)"/);

            if (shortMatch && shortMatch[1]) {
              this.shortContent = shortMatch[1];
              this.longContent = longMatch && longMatch[1] ? longMatch[1] : this.shortContent;
              return;
            }
          }
        }

        // Unrecognized shape: only ever display a string. Never assign a raw
        // object here — it would render as "[object Object]". Leaving it null
        // keeps the "Gathering data..." placeholder until a usable value arrives.
        this.shortContent = typeof data === 'string' ? data : null;
        this.longContent = this.shortContent;
        // console.log('[handleInsights] Using string as-is');
        return;
      }

      // Case 3: Any other type of data, convert to string
      this.shortContent = String(data);
      this.longContent = String(data);
      // console.log('[handleInsights] Converted to string');

    } catch (error) {
      // Final fallback in case of any unexpected errors
      console.error('[handleInsights] Error processing data:', error);
      this.shortContent = 'Error processing data';
      this.longContent = 'Error processing data';
    }
  }

  private handleExploratoryEmail(data: string): void {
    // Check if promptData is null
    if (data === null) {
      this.shortContent = null;
      this.longContent = null;
      return;
    }

    // Use the safeParseJSON utility to parse the JSON string
    const parsedData = safeParseJSON(data);

    if (parsedData) {
      // Safely assign parsed values if the JSON is valid
      this.shortContent = parsedData.description || null;

      // Replace placeholders with actual values
      let emailContent = parsedData.content || '';
      if (this.company) {
        emailContent = emailContent.replace(/\[Recipient's Name\]/g, this.company.person_name);
      }

      const senderName = this.user.name || '[Your Name]';
      emailContent = emailContent.replace(/\[Your Name\]/g, senderName);

      this.longContent = emailContent;
    } else {
      // Handle invalid JSON gracefully
      this.shortContent = 'N/A';
      this.longContent = 'N/A';
    }
  }

  /**
   * Handles the processing of role data with selection logic
   * @param promptData - The raw prompt data to process for roles
   */
  private handleRole(promptData: string): void {
    this.listContent = this.options || [];

    try {
      const parsedData = safeParseJSON(promptData) as RoleResponse;

      if (parsedData && parsedData.job_level) {
        // Store the job level
        this.jobLevel = parsedData.job_level;
        this.identifiedInterests = parsedData.identified_interests

        // Find if the job_level matches any of the options (case insensitive)
        const selectedRole = this.listContent.find(
          option => option.toLowerCase() === parsedData.job_level.toLowerCase()
        );

        // Store whether each role is selected
        this.roleSelections = this.listContent.map(role =>
          role.toLowerCase() === parsedData.job_level.toLowerCase()
        );
      } else {
        this.jobLevel = 'Unknown';
        this.roleSelections = this.listContent.map(() => false);
      }
    } catch (error) {
      console.error('Error processing role data:', error);
      this.jobLevel = 'Unknown';
      this.roleSelections = this.listContent.map(() => false);
    }
  }

  private handlePersonalBackground(data: string): void {
    let content: string;

    try {
      // Try parsing the data as JSON
      const parsedData = JSON.parse(data);

      if (typeof parsedData === 'object' && parsedData !== null) {
        // If parsedData is an object, check for 'short' or 'description'
        content = parsedData.short?.trim() || parsedData.description?.trim() || 'N/A';
      } else if (typeof parsedData === 'string') {
        // If parsedData is a plain string, use it directly
        content = parsedData.trim() || 'N/A';
      } else {
        // If parsedData is neither an object nor a string
        content = 'N/A';
      }
    } catch (error) {
      // If data cannot be parsed, assume it's a plain string and use it
      content = data.trim() || 'N/A';
    }

    this.shortContent = content;
    this.htmlContent = content;
  }

  private handleSpecialBox(data: any): void {
    // console.log('[handleSpecialBox] Data:', data);

    if (data === null) {
      this.htmlContent = null;
      this.specialContent = null;
      return;
    }

    // Handle case when data is a string (JSON format)
    if (typeof data === 'string') {
      try {
        data = JSON.parse(data);
      } catch (e) {
        console.error('Error parsing special box data:', e);
        this.htmlContent = 'Error processing data';
        this.specialContent = null;
        return;
      }
    }

    // Now data should be an object
    if (data && typeof data === 'object') {
      this.htmlContent = data.short || '';
      this.specialContent = {
        current_stage_of_the_fund: data.current_stage_of_the_fund || null,
        investment_stages: data.investment_stages || null,
        summary_investment_thesis: data.summary_investment_thesis || null,
        ticket_size: data.ticket_size || null,
        top_startups: data.top_startups || null
      }
    } else {
      this.htmlContent = 'N/A';
      this.specialContent = null;
    }
  }

  private handleEmailSequence(data: any): void {
    if (data === null) {
      this.htmlContent = null;
      return;
    }

    // Handle case when data is a string (JSON format)
    if (typeof data === 'string') {
      try {
        data = JSON.parse(data);
      } catch (e) {
        console.error('Error parsing email sequence data:', e);
        this.htmlContent = 'Error processing data';
        return;
      }
    }

    // Now data should be an object
    if (data && typeof data === 'object') {
      // Check if it has the expected email sequence structure
      if (data.emails && Array.isArray(data.emails)) {
        // Format multiple emails for display
        let emailsHtml = '';
        data.emails.forEach((email: any, index: number) => {
          emailsHtml += `
            <div class="email-item mb-4">
              <h4 class="font-semibold text-sm mb-2">Email ${index + 1}</h4>
              <p class="text-xs text-gray-600 mb-1"><strong>Subject:</strong> ${email.subject || 'No subject'}</p>
              <div class="text-sm">${email.content || email.body || 'No content'}</div>
            </div>
          `;
        });
        this.htmlContent = emailsHtml;
      } else if (data.subject && data.content) {
        // Handle single email format (similar to exploratory_email_example)
        this.htmlContent = `
          <div class="email-item">
            <p class="text-xs text-gray-600 mb-2"><strong>Subject:</strong> ${data.subject}</p>
            <div class="text-sm">${data.content}</div>
            ${data.description ? `<p class="text-xs text-gray-500 mt-2 italic">${data.description}</p>` : ''}
          </div>
        `;
      } else {
        // Fallback to general content
        this.htmlContent = data.short || data.description || data.content || 'Email sequence generated successfully';
      }
    } else {
      this.htmlContent = 'N/A';
    }
  }

  /**
   * Extracts an array from a JSON content string or object based on the specified key.
   *
   * @param jsonContent - The JSON string or object to parse.
   * @param key - The key to extract the array from.
   *
   * @author Sabin Nițu
   * @returns An array of strings extracted from the specified key,
   *          or an empty array if the input is invalid or the key does not exist.
   *
   * @description
   * This function takes a JSON string or object and attempts to extract an array from it
   * based on the provided key. If the JSON content is invalid, empty, or does not contain
   * the specified key, it logs a warning and returns an empty array. If parsing fails,
   * an error is logged, and an empty array is returned.
   */
  getFormattedList(jsonContent: string | { [key: string]: any[] }, key: string): string[] {
    // Check if jsonContent is null
    if (jsonContent === null) {
      return []; // Return an empty array to show loading
    }

    const parsedContent = typeof jsonContent === 'string' ? safeParseJSON(jsonContent) : jsonContent;
    if (!parsedContent || !Array.isArray(parsedContent[key])) {
      return []; // Return an empty array for invalid or missing data
    }

    return parsedContent[key];
  }

  /**
   * Handles pill-style data. Accepts multiple shapes so a single template can
   * render both classic interest lists and the insights array format:
   *   - { [key]: ['a', 'b'] }                        e.g. { interests: [...] }
   *   - { insights: [{ title, description }, ...] } e.g. ASK_ASSISTANT_INITIAL_OUTREACH
   *
   * Each item becomes a pill label: strings pass through; objects contribute
   * their `title`.
   */
  private handlePills(data: any, key: string): void {
    // safeParseJSON returns null for arrays; fall back to the raw value so a
    // top-level array still flows through.
    const parsedData = safeParseJSON(data) ?? data;

    const rawArray: any[] =
      Array.isArray(parsedData?.[key]) ? parsedData[key]
      : Array.isArray(parsedData?.insights) ? parsedData.insights
      : Array.isArray(parsedData) ? parsedData
      : [];

    const pills = rawArray
      .map((item) => (typeof item === 'string' ? item : item?.title))
      .filter((label): label is string => typeof label === 'string' && label.length > 0);

    this.listContent = pills.length > 0 ? pills : ['N/A'];
  }

  /**
   * Parses a JSON string to extract and format the `years` field into a human-readable string.
   *
   * @param jsonContent - The JSON string containing the `years` field.
   *
   * @author Sabin Nițu
   * @returns A formatted string representing the years (e.g., "1 year", "2 years")
   *          or `'N/A'` if the input is invalid or the field is not present.
   *
   * @description
   * This function takes a JSON string, sanitizes it for parsing, and attempts to extract
   * the `years` field. If the field exists and is a number, it formats the value as a
   * singular ("1 year") or plural ("X years") string. If parsing fails or the `years`
   * field is missing, it logs an error and returns `'N/A'`.
   */
  getFormattedYears(jsonContent: string): string {
    // If jsonContent is null, return N/A
    if (jsonContent === null) {
      return 'N/A';
    }

    const parsedContent = safeParseJSON(jsonContent);
    if (!parsedContent) {
      return 'N/A';
    }

    // First check for total_tenure_business_years
    if (typeof parsedContent?.total_tenure_business_years === 'number') {
      const years = parsedContent.total_tenure_business_years;
      return years === 1 ? `${years} year` : `${years} years`;
    }

    // Fall back to the original logic checking for 'years'
    if (typeof parsedContent?.years !== 'number') {
      return 'N/A';
    }

    const years = parsedContent.years;
    return years === 1 ? `${years} year` : `${years} years`;
  }

  /**
   * Shows the full content in a popup dialog.
   * Updated to handle insights array format.
   */
  readMore(): void {
    let contentToShow = null;
    let useWideModal = false;

    // For insights and basic_words type, check if we have insights array
    if (this.type === 'insights' || this.type === 'basic_words') {
      if (this.insightsArray && this.insightsArray.length > 0) {
        // Use wide modal for insights array
        useWideModal = true;

        // Build HTML content from insights array with grid layout
        let insightsHtml = '';

        // Add short content if available
        if (this.shortContent) {
          insightsHtml += `<div class="bg-gray-50 dark:bg-gray-800 rounded-xl p-4 mb-6">
            <p class="text-sm text-gray-700 dark:text-gray-300">${this.shortContent}</p>
          </div>`;
        }

        // Add insights in a grid
        insightsHtml += '<div class="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-4">';
        this.insightsArray.forEach((insight, index) => {
          insightsHtml += `
            <div class="bg-white dark:bg-gray-800 border border-gray-100 dark:border-gray-700 rounded-xl p-4 transition-shadow">
              <h3 class="text-sm font-semibold text-secondary dark:text-gray-100 mb-3">${insight.title}</h3>
              <p class="text-sm text-gray-700 dark:text-gray-300 leading-relaxed">${insight.description}</p>
            </div>
          `;
        });
        insightsHtml += '</div>';

        contentToShow = insightsHtml;
      } else if (this.shortContent && this.longContent && this.shortContent !== this.longContent) {
        // Original format: combine short and long
        contentToShow = `<div class="bg-gray-50 dark:bg-gray-800 rounded-xl p-3">${this.shortContent}</div><br/>${this.longContent}`;
      } else if (this.longContent) {
        contentToShow = this.longContent;
      } else if (this.shortContent) {
        contentToShow = this.shortContent;
      }
    }
    // For exploratory-email type, combine description and email content
    else if (this.type === 'exploratory-email') {
      if (this.shortContent && this.longContent) {
        contentToShow = `<div class="bg-gray-50 dark:bg-gray-800 rounded-xl p-3">${this.shortContent}</div><br/>${this.longContent}`;
      } else {
        contentToShow = this.longContent || this.shortContent;
      }
    }
    // For personal-background type, combine if both exist
    else if (this.type === 'personal-background') {
      if (this.shortContent && this.longContent && this.shortContent !== this.longContent) {
        contentToShow = `<div class="bg-gray-50 dark:bg-gray-800 rounded-xl p-3">${this.shortContent}</div><br/>${this.longContent}`;
      } else {
        contentToShow = this.longContent || this.htmlContent || this.shortContent;
      }
    }
    // For company-data type, combine short and long
    else if (this.type === 'company-data') {
      if (this.shortContent && this.longContent && this.shortContent !== this.longContent) {
        contentToShow = `<div class="bg-gray-50 dark:bg-gray-800 rounded-xl p-3">${this.shortContent}</div><br/>${this.longContent}`;
      } else {
        contentToShow = this.longContent || this.shortContent;
      }
    }
    // For special-box type, prioritize using htmlContent
    else if (this.type === 'special-box' && this.htmlContent) {
      contentToShow = this.htmlContent;
    }
    // For other types, use whatever is available
    else {
      // Check if we have both short and long content
      if (this.shortContent && this.longContent && this.shortContent !== this.longContent) {
        contentToShow = `<div class="bg-gray-50 dark:bg-gray-800 rounded-xl p-3">${this.shortContent}</div><br/>${this.longContent}`;
      } else {
        contentToShow = this.longContent || this.htmlContent || this.shortContent || this.promptContent;
      }
    }

    // Show the popup if we have content
    if (contentToShow) {
      // Use a more descriptive title for insights array
      const popupTitle = (this.type === 'insights' && this.insightsArray.length > 0)
        ? 'Detailed Insights'
        : this.title;

      // Call appropriate popup method based on whether we need wide modal
      if (useWideModal) {
        this.popupService.infoWide(popupTitle, contentToShow);
      } else {
        this.popupService.info(popupTitle, contentToShow);
      }
    }
  }

  stripHtmlAndCopy(htmlString: any): string {
    // Create a temporary div element to parse the HTML
    let tempDiv = document.createElement("div");
    tempDiv.innerHTML = htmlString;

    // Extract and return just the text content
    return tempDiv.textContent || tempDiv.innerText || '';
  }

  copyEmailContent(content: any): void {
    const emailContent = this.stripHtmlAndCopy(content);

    // Check if the Clipboard API is supported
    if (navigator.clipboard && navigator.clipboard.writeText) {
      navigator.clipboard
        .writeText(emailContent)
        .then(() => {
          // Provide feedback
          this.popupService.notify('Email content copied to clipboard!', 'Email content', 'success', 2500);
        })
        .catch((err) => {
          console.error('Failed to copy text to clipboard:', err);
        });
    } else {
      // Fallback for unsupported browsers
      this.fallbackCopyTextToClipboard(emailContent);
    }
  }

  // Fallback method for unsupported browsers
  private fallbackCopyTextToClipboard(text: string) {
    const textarea = document.createElement('textarea');
    textarea.value = text;
    textarea.style.position = 'fixed'; // Prevent scrolling to bottom
    textarea.style.opacity = '0'; // Hide the textarea
    document.body.appendChild(textarea);
    textarea.focus();
    textarea.select();
    try {
      const successful = document.execCommand('copy');
      if (successful) {
        this.popupService.notify('Email content copied to clipboard!', 'Email content', 'success');
      } else {
        console.error('Fallback: Copy command was unsuccessful');
      }
    } catch (err) {
      console.error('Fallback: Unable to copy', err);
    }
    document.body.removeChild(textarea);
  }

  // Add this new method to handle reading a specific news item
  readNewsItem(index: number): void {
    if (this.newsContent && this.newsContent[index] && this.newsContent[index].long_description) {
      this.popupService.info(this.newsContent[index].title, this.newsContent[index].long_description);
    }
  }

  // Add this new method to your component class
  private handleCompanyNews(data: string): void {
    // Check if data is null
    if (data === null) {
      this.newsContent = [];
      return;
    }

    // Use the safeParseJSON utility to parse the JSON string
    const parsedData = safeParseJSON(data);

    if (parsedData && parsedData.items && Array.isArray(parsedData.items)) {
      this.newsContent = parsedData.items;
    } else {
      // Handle invalid JSON gracefully
      console.error('Invalid company news data:', data);
      this.newsContent = [];
    }
  }

  /**
   * Handles company data format by populating shortContent and longContent
   * @param data - The raw company data to process
   */
  private handleCompanyData(data: any): void {
    // Check if data is null - keep in loading state
    if (data === null || data === undefined) {
      this.shortContent = null;
      this.longContent = null;
      return;
    }

    try {
      let parsedData = data;

      // If data is a string, try to parse it
      if (typeof data === 'string') {
        parsedData = safeParseJSON(data);
      }

      // Handle case where API wraps data in a 'data' property
      if (parsedData && typeof parsedData === 'object' && 'data' in parsedData) {
        parsedData = parsedData.data;
      }

      // Handle response wrapper format
      if (parsedData && typeof parsedData === 'object' && 'response' in parsedData) {
        parsedData = parsedData.response;
      }

      // Now check if we have the expected structure
      if (parsedData && typeof parsedData === 'object') {
        if ('short' in parsedData) {
          // Populate the standard content properties
          this.shortContent = parsedData.short || null;
          this.longContent = parsedData.long || parsedData.description || null;
          return;
        }
      }

      // If we get here, data structure is unexpected - stay in loading state
      console.warn('[handleCompanyData] Unexpected data structure, keeping loading state');
      this.shortContent = null;
      this.longContent = null;

    } catch (error) {
      console.error('[handleCompanyData] Error parsing company data:', error);
      // On error, stay in loading state rather than showing error
      this.shortContent = null;
      this.longContent = null;
    }
  }
}
